/*
 * Decorative 3D / parallax helpers. All purely decorative: rendered behind
 * content (z-0), pointer-events-none and low opacity so they NEVER reduce text
 * readability. CSS/SVG based for performance.
 */
import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ASSETS } from "@/lib/site";

// Optional full-bleed section background illustration (Higgsfield asset).
// Renders nothing when no src is configured, so sections keep their CSS/SVG
// look until an asset URL is added. A strong brand gradient overlay keeps all
// text fully legible over any artwork.
export const SectionBg = ({ src, opacity = 0.5 }) => {
  if (!src) return null;
  return (
    <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none" data-asset="section-bg" aria-hidden="true">
      <img src={src} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" style={{ opacity }} />
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(180deg, rgba(2,4,10,0.84) 0%, rgba(2,4,10,0.5) 45%, rgba(2,4,10,0.92) 100%)" }}
      />
    </div>
  );
};

// Gentle scroll parallax. Outer div is the tracked (stable) element; inner moves.
export const Parallax = ({ children, from = 28, to = -28, className = "" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [from, to]);
  return (
    <div ref={ref} className={className} aria-hidden="true">
      <motion.div style={{ y }}>{children}</motion.div>
    </div>
  );
};

// Small floating glass data card used as ambient 3D decoration.
export const DataCard = ({ label, value, tone = "blue", className = "", style }) => (
  <div
    className={`glass rounded-xl px-3 py-2 shadow-[0_24px_50px_-24px_rgba(0,0,0,0.8)] ${className}`}
    style={style}
    aria-hidden="true"
  >
    <div className="font-mono text-[8px] uppercase tracking-[0.2em] text-[var(--muted-2)]">{label}</div>
    <div className={`font-mono text-xs ${tone === "green" ? "text-[var(--green)]" : "text-[var(--blue-bright)]"}`}>{value}</div>
  </div>
);

// Decorative node graph with animated trace lines. Swaps to a Higgsfield asset
// image when ASSETS.decorGraph is configured.
export const NodeGraph = ({ className = "" }) => {
  if (ASSETS.decorGraph) {
    return (
      <img
        src={ASSETS.decorGraph}
        alt=""
        loading="lazy"
        aria-hidden="true"
        data-asset="decor-graph"
        className={className}
      />
    );
  }
  return (
  <svg viewBox="0 0 220 160" className={className} fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="decor-edge" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#3d7bff" />
        <stop offset="1" stopColor="#36e2a0" />
      </linearGradient>
    </defs>
    {["M20,30 C80,30 80,80 140,80", "M20,80 C80,80 80,80 140,80", "M20,130 C80,130 80,80 140,80", "M140,80 C185,80 185,45 205,45", "M140,80 C185,80 185,120 205,120"].map((d, i) => (
      <path key={i} d={d} stroke="url(#decor-edge)" strokeWidth="1.2" className="trace-line" opacity="0.7" />
    ))}
    {[30, 80, 130].map((cy, i) => (
      <g key={i}>
        <circle cx="20" cy={cy} r="5.5" fill="#0b111e" stroke="#3d7bff" strokeWidth="1.4" />
        <circle cx="20" cy={cy} r="2" fill="#5b9cff" />
      </g>
    ))}
    <rect x="129" y="69" width="22" height="22" rx="6" fill="#0b111e" stroke="#5b9cff" strokeWidth="1.4" />
    <circle cx="140" cy="80" r="2.6" fill="#36e2a0" />
    {[45, 120].map((cy, i) => (
      <circle key={i} cx="205" cy={cy} r="5.5" fill="#0b111e" stroke="#36e2a0" strokeWidth="1.4" />
    ))}
  </svg>
  );
};

// Floating glass panel with a tiny sparkline + doc lines. Purely ambient 3D
// decoration: rotated in 3D, low opacity, never holds real content. Swaps to a
// Higgsfield asset image (inside the same glass frame) when ASSETS.decorPanel
// is configured.
export const FloatPanel = ({ title = "module", className = "", style }) => {
  if (ASSETS.decorPanel) {
    return (
      <div
        className={`float-panel glass rounded-2xl overflow-hidden w-[150px] shadow-[0_30px_70px_-30px_rgba(0,0,0,0.85)] ${className}`}
        style={style}
        aria-hidden="true"
        data-asset="decor-panel"
      >
        <img src={ASSETS.decorPanel} alt="" loading="lazy" className="block w-full h-auto" />
      </div>
    );
  }
  return (
  <div
    className={`float-panel glass rounded-2xl p-3 w-[150px] shadow-[0_30px_70px_-30px_rgba(0,0,0,0.85)] ${className}`}
    style={style}
    aria-hidden="true"
  >
    <div className="flex items-center justify-between">
      <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-[var(--muted-2)]">{title}</span>
      <span className="h-1.5 w-1.5 rounded-full bg-[var(--green)] animate-pulse-soft" />
    </div>
    <svg viewBox="0 0 120 40" className="mt-2 w-full" fill="none">
      <defs>
        <linearGradient id="fp-line" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#3d7bff" />
          <stop offset="1" stopColor="#36e2a0" />
        </linearGradient>
      </defs>
      <polyline points="0,30 20,22 40,26 60,12 80,18 100,6 120,10" stroke="url(#fp-line)" strokeWidth="1.6" className="trace-line" />
    </svg>
    <div className="mt-2 space-y-1">
      {[82, 56, 70].map((w, i) => (
        <span key={i} className="block h-1 rounded-full bg-[var(--line-strong)]" style={{ width: `${w}%` }} />
      ))}
    </div>
  </div>
  );
};
